import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { BufferService, filtersSortService, M_DrawerTableTextField, M_InputTextField } from 'src/M_Components';
    import { API_BASE_URL } from '../../M_Components/M_Utils/services.config';
import { LoadingContext } from '../../M_Components/M_Utils/loading.context';
import './ediPage.css';

const EDIPage = () => {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const lookupPageSize = 25;
  const [approvedProcessStatusId, setApprovedProcessStatusId] = useState('');
  const approvedBookingFilter = approvedProcessStatusId
    ? `ProcessKey ne '' and ProcessStatusId eq ${approvedProcessStatusId}`
    : "ProcessKey ne ''";
  const createEmptyConfig = (messageType = '', messageSubType = '') => ({
    id: undefined,
    messageType,
    messageSubType,
    messageDestinationEmail: '',
    messageSenderCode: '',
    messageSenderEori: '',
    messageSenderContact: '',
    messageSenderEmail: '',
    messageReceiverCode: '',
    carrierAgentCode: '',
    code001: '',
    code002: '',
    code003: '',
    code004: '',
    code005: '',
    code006: '',
    code007: '',
    code008: '',
    code009: '',
    code010: '',
    code031: '',
    outputFolderPath: '',
    outputFileExtension: '',
    outputFileCounter: 0
  });
  const mapConfigFromApi = (config) => ({
    id: config.Id,
    messageType: config.MessageType,
    messageSubType: config.MessageSubType,
    messageDestinationEmail: config.MessageDestinationEmail,
    messageSenderCode: config.MessageSenderCode,
    messageSenderEori: config.MessageSenderEori,
    messageSenderContact: config.MessageSenderContact,
    messageSenderEmail: config.MessageSenderEmail,
    messageReceiverCode: config.MessageReceiverCode,
    carrierAgentCode: config.CarrierAgentCode,
    code001: config.Code001,
    code002: config.Code002,
    code003: config.Code003,
    code004: config.Code004,
    code005: config.Code005,
    code006: config.Code006,
    code007: config.Code007,
    code008: config.Code008,
    code009: config.Code009,
    code010: config.Code010,
    code031: config.Code031,
    outputFolderPath: config.OutputFolderPath,
    outputFileExtension: config.OutputFileExtension,
    outputFileCounter: config.OutputFileCounter,
    dbStatus: config.DbStatus,
    tenantId: config.TenantId,
    createdBy: config.CreatedBy,
    createdOn: config.CreatedOn,
    lastUpdate: config.LastUpdate,
    lastUpdateBy: config.LastUpdateBy
  });
  const [messageTypes, setMessageTypes] = useState({ SIMTEMTypes: [], ICS2Types: [] });
  const [selectedMessageType, setSelectedMessageType] = useState(() => {
    return localStorage.getItem('ediSelectedMessageType') || 'SIMTEM';
  });
  const [selectedSubType, setSelectedSubType] = useState('');
  const [processSearchTerm, setProcessSearchTerm] = useState('');
  const [bookingProcessOptions, setBookingProcessOptions] = useState([]);
  const [bookingProcessPage, setBookingProcessPage] = useState(1);
  const [bookingProcessTotalItems, setBookingProcessTotalItems] = useState(0);
  const [hasLoadedBookingProcesses, setHasLoadedBookingProcesses] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [selectedVoyage, setSelectedVoyage] = useState(null);
  const [voyageSearchTerm, setVoyageSearchTerm] = useState('');
  const [voyageOptions, setVoyageOptions] = useState([]);
  const [voyagePage, setVoyagePage] = useState(1);
  const [voyageTotalItems, setVoyageTotalItems] = useState(0);
  const [hasLoadedVoyages, setHasLoadedVoyages] = useState(false);
  const [voyageDetails, setVoyageDetails] = useState({
    vesselCode: '',
    vesselName: '',
  });
  const [messageFunctionCode, setMessageFunctionCode] = useState(9);
  const simtemMessageFunctions = [
    { value: 9, labelKey: 'EdiMessageFunctionOriginal' },
    { value: 2, labelKey: 'EdiMessageFunctionAddition' },
    { value: 4, labelKey: 'EdiMessageFunctionChange' },
    { value: 1, labelKey: 'EdiMessageFunctionCancellation' },
    { value: 55, labelKey: 'EdiMessageFunctionCancelBl' },
  ];
  const [generatedFiles, setGeneratedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [msgConfig, setMsgConfig] = useState(createEmptyConfig('SIMTEM', ''));

  const baseUrl = API_BASE_URL.main.endsWith('/') ? API_BASE_URL.main : `${API_BASE_URL.main}/`;
  const isSIMTEM = selectedMessageType === 'SIMTEM';
  const isICS2 = selectedMessageType === 'ICS2';
  const SUPPORTED_SIMTEM_TYPES = ['IFCSUM_PCOMLEI', 'IFCSUM_PCOMLIS'];
  const SUPPORTED_ICS2_TYPES = ['IE3F10', 'IE3A10', 'IE3Q04'];

  const mergeMessageSubtypes = (fromApi, supported) => [
    ...new Set([...supported, ...(fromApi || [])]),
  ];

  const normalizeMessageTypes = (data) => {
    const simtem = data?.simtemTypes ?? data?.SIMTEMTypes ?? [];
    const ics2 = data?.ics2Types ?? data?.ICS2Types ?? [];
    return {
      SIMTEMTypes: mergeMessageSubtypes(
        simtem.filter((type) => type !== 'EDI_IFCSUM93A' && type !== 'EDI_IFCSUM'),
        SUPPORTED_SIMTEM_TYPES,
      ),
      ICS2Types: mergeMessageSubtypes(ics2, SUPPORTED_ICS2_TYPES),
    };
  };

  const getSubtypesForMessageType = (messageType, availableTypes = messageTypes) =>
    messageType === 'SIMTEM'
      ? (availableTypes.SIMTEMTypes || [])
      : (availableTypes.ICS2Types || []);

  const flattenValidationErrors = (errors) => {
    const messages = Object.values(errors || {})
      .flat()
      .filter(Boolean);

    return messages.join(' ');
  };

  const extractMessagesFromPayload = (payload) => {
    const messages = payload?.messages || payload?.Messages;
    if (Array.isArray(messages)) {
      return formatProcessErrorList(messages);
    }

    return '';
  };

  const formatProcessErrorList = (errors) => {
    if (Array.isArray(errors)) {
      return errors.filter(Boolean).join('\n\n');
    }

    if (typeof errors !== 'string' || !errors.trim()) {
      return '';
    }

    const parts = errors
      .split(/(?=Part\s+[^\s:]+:)/i)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length > 1) {
      return parts.join('\n\n');
    }

    return errors.trim();
  };

  const tryParseStructuredError = (errorText) => {
    if (typeof errorText !== 'string') {
      return '';
    }

    let normalized = errorText.trim();
    const brokerPrefix = normalized.match(/BrokerTransporterAllEntitiesAPI error:\s*\w+\s*-\s*/i);
    if (brokerPrefix?.[0]) {
      normalized = normalized.slice(brokerPrefix.index + brokerPrefix[0].length).trim();
    }

    const jsonStart = normalized.indexOf('{');
    if (jsonStart < 0) {
      return normalized;
    }

    try {
      const parsed = JSON.parse(normalized.slice(jsonStart));
      return (
        extractMessagesFromPayload(parsed)
        || flattenValidationErrors(parsed?.errors)
        || parsed?.message
        || parsed?.title
        || normalized
      );
    } catch {
      return normalized;
    }
  };

  const normalizeApiErrorMessage = (err, fallbackMessage) => {
    const responseData = err?.response?.data;

    if (typeof responseData === 'string') {
      return tryParseStructuredError(responseData) || responseData || fallbackMessage;
    }

    if (responseData && typeof responseData === 'object') {
      const brokerMessages = extractMessagesFromPayload(responseData);
      if (brokerMessages) {
        return brokerMessages;
      }
    }

    if (responseData?.errors) {
      return flattenValidationErrors(responseData.errors) || responseData.title || fallbackMessage;
    }

    if (responseData?.message) {
      return responseData.message;
    }

    if (responseData?.title) {
      return responseData.title;
    }

    if (err?.response?.statusText) {
      return err.response.statusText;
    }

    if (err?.message) {
      return tryParseStructuredError(err.message) || err.message;
    }

    return fallbackMessage;
  };

  const simplifyClientErrorMessage = (message) => {
    if (!message || typeof message !== 'string') {
      return 'Ocorreu um erro ao gerar o EDI.';
    }

    const parsedMessage = tryParseStructuredError(message) || message;
    const formattedMessage = formatProcessErrorList(parsedMessage);
    const withoutStack = formattedMessage
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith('at '))
      .join('\n')
      .replace(/[ \t]+/g, ' ')
      .trim();

    const processErrors = withoutStack
      .split(/\n\s*\n/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (processErrors.length > 1) {
      return processErrors.join('\n\n');
    }

    const dataValidationMatch = withoutStack.match(/Data Validation Error:[^.]+\./i);
    if (dataValidationMatch?.[0] && processErrors.length <= 1) {
      return dataValidationMatch[0].trim();
    }

    return withoutStack;
  };

  const showErrorMessage = (message) => {
    if (!message) {
      return;
    }

    const simplifiedMessage = simplifyClientErrorMessage(message);
    setStatusType('error');
    setStatusMessage(simplifiedMessage);
    window.setTimeout(() => setStatusMessage(''), 15000);

    if (typeof ctx.toastMessages === 'function') {
      ctx.toastMessages('error', '', simplifiedMessage);
    }
  };

  const showSuccessMessage = (message) => {
    setStatusType('success');
    setStatusMessage(message);
    window.setTimeout(() => setStatusMessage(''), 5000);
  };

  useEffect(() => {
    loadMessageTypes();
  }, []);

  useEffect(() => {
    const loadApprovedProcessStatus = async () => {
      try {
        const processStatusService = new BufferService('processStatus');
        const response = await processStatusService.get();
        const approvedStatus = (response?.Items || []).find((item) => item?.StatusName === 'Approved');
        setApprovedProcessStatusId(approvedStatus?.Id || '');
      } catch (err) {
        console.error(err);
      }
    };

    loadApprovedProcessStatus();
  }, []);

  useEffect(() => {
    const availableSubTypes = getSubtypesForMessageType(selectedMessageType);

    if (availableSubTypes.length === 0) {
      return;
    }

    if (!selectedSubType || !availableSubTypes.includes(selectedSubType)) {
      setSelectedSubType(availableSubTypes[0]);
    }
  }, [messageTypes, selectedMessageType, selectedSubType]);

  useEffect(() => {
    if (selectedMessageType && selectedSubType) {
      loadMessageTypeConfig(selectedMessageType, selectedSubType);
    } else {
      setMsgConfig(createEmptyConfig(selectedMessageType, selectedSubType));
    }
  }, [selectedMessageType, selectedSubType]);

  const loadMessageTypeConfig = async (messageType, messageSubType) => {
    try {
      const response = await axios.get(`${baseUrl}MessageTypeConfig/byMessageType/${messageType}/${messageSubType}`);
      if (response.data) {
        setMsgConfig(mapConfigFromApi(response.data));
      } else {
        setMsgConfig(createEmptyConfig(messageType, messageSubType));
        showErrorMessage('MessageType config not found. Please configure it first.');
      }
    } catch (err) {
      setMsgConfig(createEmptyConfig(messageType, messageSubType));
      if (err.response?.status === 404) {
        return;
      }
      showErrorMessage('Failed to load MessageType config');
      console.error(err);
    }
  };

  const loadMessageTypes = async () => {
    try {
      const response = await axios.get(`${baseUrl}EDI/GetMessageTypes`);
      setMessageTypes(normalizeMessageTypes(response.data));
    } catch (err) {
      showErrorMessage('Failed to load message types');
      console.error(err);
    }
  };

  const clearVoyageDetails = () => {
    setVoyageDetails({
      vesselCode: '',
      vesselName: '',
    });
  };

  const mapFallbackBooking = (booking) => ({
    Id: booking.Id,
    ProcessKey: booking.ProcessKey,
    BookingNumber: booking.BookingNumber || booking.ProcessKey,
    ClientReference: booking.ClientReference || '',
    ClientName: booking.ClientName || booking.Client || '',
    VoyageNumber: booking.VoyageNumber || '',
    LoadingPortName: booking.LocationLoadingPort || booking.LoadingPortCode || '',
    DischargePortName: booking.LocationDischargePort || booking.DischargePortCode || '',
    BLNumber: booking.BLNumber || '',
  });

  const mapFallbackVoyage = (voyage) => ({
    Id: voyage.Id,
    VoyageNumber: voyage.VoyageNumber || '',
    VesselCode: voyage.VesselCode || voyage.Vessel?.VesselCode || '',
    VesselName: voyage.VesselName || voyage.Vessel?.VesselName || '',
  });

  const loadBookingProcesses = async (searchTerm = '', page = 1) => {
    try {
      const response = await axios.get(`${baseUrl}EDI/GetBookingProcessSummaries`, {
        params: {
          searchTerm,
          page,
          take: lookupPageSize,
        }
      });
      setBookingProcessOptions(response.data?.Items || []);
      setBookingProcessTotalItems(response.data?.TotalItems || 0);
      setBookingProcessPage(response.data?.Page || page);
      setHasLoadedBookingProcesses(true);
    } catch (err) {
      if (err.response?.status === 404) {
        await loadBookingProcessesFallback(searchTerm, page);
        return;
      }

      showErrorMessage('Failed to load booking processes');
      console.error(err);
    }
  };

  const loadBookingProcessesFallback = async (searchTerm = '', page = 1) => {
    try {
      const bookingService = new BufferService('Booking');
      const filterService = new filtersSortService();
      const filters = searchTerm
        ? [{ name: 'BookingNumber', type: 'string', operation: 'Contains', value: searchTerm }]
        : null;
      const query = filterService.queryBuilder(page, lookupPageSize, { columnName: 'CreatedOn', orderbool: true }, filters, null);
      const response = await bookingService.filter(query);
      const items = (response?.Items || []).filter((booking) =>
        booking?.ProcessKey && booking?.BookingStatus === 'Approved'
      );

      setBookingProcessOptions(items.map(mapFallbackBooking));
      setBookingProcessTotalItems(response?.TotalItems || items.length);
      setBookingProcessPage(page);
      setHasLoadedBookingProcesses(true);
    } catch (fallbackErr) {
      showErrorMessage('Failed to load booking processes');
      console.error(fallbackErr);
    }
  };

  const loadVoyages = async (searchTerm = '', page = 1) => {
    try {
      const response = await axios.get(`${baseUrl}EDI/GetVoyageSummaries`, {
        params: {
          searchTerm,
          page,
          take: lookupPageSize,
        }
      });
      setVoyageOptions(response.data?.Items || []);
      setVoyageTotalItems(response.data?.TotalItems || 0);
      setVoyagePage(response.data?.Page || page);
      setHasLoadedVoyages(true);
    } catch (err) {
      if (err.response?.status === 404) {
        await loadVoyagesFallback(searchTerm, page);
        return;
      }

      showErrorMessage('Failed to load voyages');
      console.error(err);
    }
  };

  const loadVoyagesFallback = async (searchTerm = '', page = 1) => {
    try {
      const voyageService = new BufferService('Voyage');
      const filterService = new filtersSortService();
      const filters = searchTerm
        ? [{ name: 'VoyageNumber', type: 'string', operation: 'Contains', value: searchTerm }]
        : null;
      const query = filterService.queryBuilder(page, lookupPageSize, { columnName: 'CreatedOn', orderbool: true }, filters, null);
      const response = await voyageService.filter(query);
      setVoyageOptions((response?.Items || []).map(mapFallbackVoyage));
      setVoyageTotalItems(response?.TotalItems || response?.Items?.length || 0);
      setVoyagePage(page);
      setHasLoadedVoyages(true);
    } catch (fallbackErr) {
      showErrorMessage('Failed to load voyages');
      console.error(fallbackErr);
    }
  };

  const handleBookingProcessSearch = (searchTerm) => {
    setProcessSearchTerm(searchTerm);
    loadBookingProcesses(searchTerm, 1);
  };

  const handleVoyageSearch = (searchTerm) => {
    setVoyageSearchTerm(searchTerm);
    loadVoyages(searchTerm, 1);
  };

  const addSelectedBooking = (booking) => {
    if (!booking?.ProcessKey) {
      return;
    }

    const normalizedBooking = mapFallbackBooking(booking);
    setSelectedBookings((current) => {
      if (current.some((item) => item.ProcessKey === normalizedBooking.ProcessKey)) {
        return current;
      }

      return [...current, normalizedBooking];
    });
  };

  const removeSelectedBooking = (bookingProcessKey) => {
    setSelectedBookings((current) => current.filter((item) => item.ProcessKey !== bookingProcessKey));
  };

  const normalizeGeneratedPath = (path) => (path || '').replace(/\\\\/g, '\\');

  const inferGeneratedFileContentType = (fileName) => {
    const extension = (fileName || '').split('.').pop()?.toLowerCase();
    if (extension === 'txt') {
      return 'text/plain';
    }
    if (extension === 'xml') {
      return 'application/xml';
    }
    return 'application/octet-stream';
  };

  const mapGeneratedFilesFromResponse = (data) => {
    if (!data) {
      return [];
    }

    const mapEntry = (entry) => {
      const outputPath = normalizeGeneratedPath(entry.filePath || entry.FilePath);
      const downloadPath = normalizeGeneratedPath(
        entry.downloadFilePath || entry.DownloadFilePath || outputPath,
      );
      return {
        processKey: entry.processKey || entry.ProcessKey || '',
        filePath: outputPath,
        downloadFilePath: downloadPath,
        fileName: entry.fileName || entry.FileName || outputPath.split('\\').pop() || 'EDI.txt',
      };
    };

    if (typeof data === 'string') {
      const normalizedPath = normalizeGeneratedPath(data);
      return mapEntry({ filePath: normalizedPath, fileName: normalizedPath.split('\\').pop() || 'ICS2.xml' });
    }

    const simtemPath = data.filePath || data.FilePath;
    if (simtemPath) {
      return [mapEntry({
        filePath: simtemPath,
        downloadFilePath: data.downloadFilePath || data.DownloadFilePath,
        fileName: simtemPath.split('\\').pop() || 'SIMTEM.txt',
      })];
    }

    const files = data.files || data.Files || [];
    return files.map((file) => mapEntry(file));
  };

  const downloadAndOpenGeneratedFile = async (generatedFile) => {
    const downloadPath = generatedFile?.downloadFilePath || generatedFile?.filePath;
    if (!downloadPath) {
      return;
    }

    const fileName = generatedFile.fileName || 'EDI.txt';
    const response = await axios.get(`${baseUrl}EDI/DownloadGeneratedFile`, {
      params: { filePath: downloadPath },
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'] || inferGeneratedFileContentType(fileName);
    const blob = new Blob([response.data], { type: contentType });
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (fileName.toLowerCase().endsWith('.xml')) {
      window.open(objectUrl, '_blank');
    }

    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
  };

  const applyVoyageDetails = (voyage) => {
    if (!voyage) {
      clearVoyageDetails();
      return;
    }

    setVoyageDetails({
      vesselCode: voyage.VesselCode || '',
      vesselName: voyage.VesselName || '',
    });
  };

  const handleGenerateSIMTEM = async () => {
    if (!selectedVoyage?.Id) {
      showErrorMessage('Voyage is required');
      return;
    }
    setLoading(true);
    setStatusMessage('');
    setGeneratedFiles([]);

    const request = {
      ...msgConfig,
      voyageId: selectedVoyage.Id,
      messageType: 'SIMTEM',
      messageSubType: selectedSubType,
      messageFunctionCode,
    };

    try {
      const response = await axios.post(`${baseUrl}EDI/GenerateSIMTEM`, request);
      const generated = mapGeneratedFilesFromResponse(response.data);
      const warningSummary = response.data?.warningSummary || response.data?.WarningSummary;
      setGeneratedFiles(generated);

      if (generated.length > 0) {
        const baseSuccess = `${generated.length} ficheiro(s) gerado(s) com sucesso.`;
        const warningText = typeof warningSummary === 'string' ? warningSummary.trim() : '';
        const successText = warningText ? `${baseSuccess} ${warningText}` : baseSuccess;
        showSuccessMessage(successText);
      }
    } catch (err) {
      showErrorMessage(normalizeApiErrorMessage(err, 'Failed to generate SIMTEM EDI'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateICS2 = async () => {
    if (!selectedBookings.length) {
      showErrorMessage('Seleciona pelo menos um booking process.');
      return;
    }
    if (!selectedSubType) {
      showErrorMessage('ICS2 message subtype is required');
      return;
    }
    setLoading(true);
    setStatusMessage('');
    setGeneratedFiles([]);

    try {
      const request = {
        ...msgConfig,
        processKeys: selectedBookings.map((booking) => booking.ProcessKey),
        messageType: 'ICS2',
        messageSubType: selectedSubType,
      };

      const response = await axios.post(`${baseUrl}EDI/GenerateICS2`, request);
      const generated = mapGeneratedFilesFromResponse(response.data);
      const errors = response.data?.errors || response.data?.Errors || [];

      setGeneratedFiles(generated);

      if (generated.length > 0) {
        showSuccessMessage(`${generated.length} ficheiro(s) gerado(s) com sucesso.`);
      }

      if (errors.length > 0) {
        showErrorMessage(formatProcessErrorList(errors));
      }
    } catch (err) {
      showErrorMessage(normalizeApiErrorMessage(err, 'Failed to generate ICS2 EDI'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyGeneratedPath = async (path) => {
    if (!path) return;
    try {
      await navigator.clipboard.writeText(path);
      showSuccessMessage('Caminho do ficheiro copiado.');
    } catch {
      showErrorMessage('Não foi possível copiar o caminho do ficheiro.');
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedMessageType || !selectedSubType) {
      showErrorMessage('Please select a message type and subtype first');
      return;
    }
    setLoading(true);
    setStatusMessage('');

    try {
      const configToSave = {
        ...msgConfig,
        messageType: selectedMessageType,
        messageSubType: selectedSubType,
        dbStatus: msgConfig.dbStatus || 'A',
        tenantId: msgConfig.tenantId || ctx.TENANTID || '00000000-0000-0000-0000-000000000000'
      };

      let response;
      if (msgConfig.id) {
        response = await axios.put(`${baseUrl}MessageTypeConfig/${msgConfig.id}`, configToSave);
      } else {
        response = await axios.post(`${baseUrl}MessageTypeConfig`, configToSave);
      }

      if (response.data) {
        setMsgConfig(mapConfigFromApi(response.data));
        showSuccessMessage('Configuration saved successfully');
      }
    } catch (err) {
      showErrorMessage(normalizeApiErrorMessage(err, 'Failed to save configuration'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edi-page">
      <div className="edi-header">
        <h1>{t('EdiPageTitle')}</h1>
      </div>

      <div className="edi-content">
        {/* QUADRADO 1: Form de Seleção Base */}
        <div className="edi-card">
          <h3>{t('EdiSelection')}</h3>

          <div className="form-group">
            <label>{t('EdiMessageType')}</label>
            <select
              value={selectedMessageType}
              onChange={(e) => {
                setSelectedMessageType(e.target.value);
                setSelectedSubType('');
                setSelectedBookings([]);
                setGeneratedFiles([]);
                localStorage.setItem('ediSelectedMessageType', e.target.value);
              }}
            >
              <option value="SIMTEM">SIMTEM (IFCSUM)</option>
              <option value="ICS2">ICS2</option>
            </select>
          </div>

          {isSIMTEM && (
            <>
              <div className="form-group">
                <M_DrawerTableTextField
                  size="100%"
                  label={t('Voyage')}
                  tableLabel={t('EdiVoyages')}
                  tooltipAdd={hasLoadedVoyages ? '' : t('EdiLoadVoyages')}
                  entity="Voyage"
                  drawerUseEntityTable
                  hiddenColumns={['Status']}
                  value={selectedVoyage}
                  staticOptions={voyageOptions}
                  staticTableColumns={[
                    { field: 'VoyageNumber', header: t('Voyage') },
                    { field: 'VesselCode', header: t('VesselCode') },
                    { field: 'VesselName', header: t('VesselName') }
                  ]}
                  filterField="VoyageNumber"
                  labelFields={['VoyageNumber']}
                  disabled={loading}
                  onDrawerOpen={() => {
                    if (!hasLoadedVoyages) {
                      loadVoyages(voyageSearchTerm, voyagePage);
                    }
                  }}
                  onSearchInput={handleVoyageSearch}
                  drawerSearchValue={voyageSearchTerm}
                  onDrawerSearchChange={handleVoyageSearch}
                  drawerPage={voyagePage}
                  drawerPageSize={lookupPageSize}
                  drawerTotalItems={voyageTotalItems}
                  onDrawerPageChange={(page) => loadVoyages(voyageSearchTerm, page)}
                  onSubmitValue={(obj) => {
                    const normalizedVoyage = obj ? mapFallbackVoyage(obj) : null;
                    setSelectedVoyage(normalizedVoyage);
                    applyVoyageDetails(normalizedVoyage);
                  }}
                />
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <M_InputTextField
                    size="100%"
                    disabled
                    label={t('VesselCode')}
                    value={voyageDetails.vesselCode}
                  />
                </div>
                <div className="form-group">
                  <M_InputTextField
                    size="100%"
                    disabled
                    label={t('VesselName')}
                    value={voyageDetails.vesselName}
                  />
                </div>
              </div>
            </>
          )}

          {isICS2 && (
            <>
              <div className="form-group">
                <M_DrawerTableTextField
                  size="100%"
                  label={t('EdiAddBookingProcess')}
                  tableLabel={t('EdiBookingProcesses')}
                  tooltipAdd={hasLoadedBookingProcesses ? '' : t('EdiLoadBookingProcesses')}
                  entity="Booking"
                  FILTER={approvedBookingFilter}
                  drawerUseEntityTable
                  hiddenColumns={['Status']}
                  value={null}
                  staticOptions={bookingProcessOptions}
                  staticTableColumns={[
                    { field: 'BookingNumber', header: t('EdiBookingNumber') },
                    { field: 'ProcessKey', header: t('EdiProcessKey') },
                    { field: 'ClientReference', header: t('EdiClientReference') },
                    { field: 'ClientName', header: t('EdiClientName') },
                    { field: 'VoyageNumber', header: t('Voyage') }
                  ]}
                  filterField="BookingNumber"
                  labelFields={['BookingNumber']}
                  disabled={loading}
                  onDrawerOpen={() => {
                    if (!hasLoadedBookingProcesses) {
                      loadBookingProcesses(processSearchTerm, bookingProcessPage);
                    }
                  }}
                  onSearchInput={handleBookingProcessSearch}
                  drawerSearchValue={processSearchTerm}
                  onDrawerSearchChange={handleBookingProcessSearch}
                  drawerPage={bookingProcessPage}
                  drawerPageSize={lookupPageSize}
                  drawerTotalItems={bookingProcessTotalItems}
                  onDrawerPageChange={(page) => loadBookingProcesses(processSearchTerm, page)}
                  onSubmitValue={(obj) => {
                    addSelectedBooking(obj);
                  }}
                />
              </div>

              {selectedBookings.length > 0 && (
                <div className="selected-processes-panel">
                  <div className="selected-processes-header">
                    <label>{t('EdiSelectedProcesses', { count: selectedBookings.length })}</label>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => setSelectedBookings([])}
                      disabled={loading}
                    >
                      {t('EdiClearAll')}
                    </button>
                  </div>
                  <div className="selected-processes-list">
                    {selectedBookings.map((booking) => (
                      <div key={booking.ProcessKey} className="selected-process-item">
                        <div className="selected-process-main">
                          <strong>{booking.BookingNumber || booking.ProcessKey}</strong>
                          <span>{booking.ProcessKey}</span>
                        </div>
                        <button
                          type="button"
                          className="btn-link danger"
                          onClick={() => removeSelectedBooking(booking.ProcessKey)}
                          disabled={loading}
                        >
                          {t('Remove')}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="form-group">
            <label>{t('EdiMessageSubtype')}</label>
            <select
              value={selectedSubType}
              onChange={(e) => setSelectedSubType(e.target.value)}
              disabled={!selectedMessageType}
            >
              <option value="">{t('EdiSelectSubtype')}</option>
              {selectedMessageType === 'SIMTEM' &&
                messageTypes.SIMTEMTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              {selectedMessageType === 'ICS2' &&
                messageTypes.ICS2Types.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
            </select>
          </div>

          {isSIMTEM && (
            <div className="form-group">
              <label>{t('EdiMessageFunction')}</label>
              <select
                value={messageFunctionCode}
                onChange={(e) => setMessageFunctionCode(Number(e.target.value))}
              >
                {simtemMessageFunctions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {t(item.labelKey)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-actions">
            {selectedMessageType === 'SIMTEM' ? (
              <button
                className="btn btn-primary"
                onClick={handleGenerateSIMTEM}
                disabled={loading || !selectedVoyage?.Id}
              >
                {loading ? t('EdiGenerating') : t('EdiGenerateSimtem')}
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleGenerateICS2}
                disabled={loading || !selectedBookings.length}
              >
                {loading ? t('EdiGenerating') : t('EdiGenerateIcs2')}
              </button>
            )}
          </div>

          {statusMessage && <div className={`status-message ${statusType === 'error' ? 'error' : 'success'}`}>{statusMessage}</div>}

          {generatedFiles.length > 0 && (
            <div className="generated-path-panel">
              <label>{t('EdiGeneratedFiles')}</label>
              {generatedFiles.map((generatedFile) => (
                <div key={`${generatedFile.processKey}-${generatedFile.filePath}`} className="generated-file-item">
                  <div className="generated-file-title">
                    {generatedFile.processKey ? `${generatedFile.processKey} - ` : ''}
                    {generatedFile.fileName}
                  </div>
                  <div className="generated-path-value">{generatedFile.filePath}</div>
                  <div className="generated-file-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => downloadAndOpenGeneratedFile(generatedFile)}
                    >
                      {t('EdiDownload')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleCopyGeneratedPath(generatedFile.filePath)}
                    >
                      {t('EdiCopyPath')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* QUADRADO 2: Configurações Avançadas Separadas */}
        <div className="edi-card">
          <h3>{t('EdiConfigTitle')}</h3>
          {selectedMessageType && selectedSubType && (
            <p className="config-info">
              {t('EdiConfigTypeSubtype', { messageType: selectedMessageType, subType: selectedSubType })}
            </p>
          )}
          <div className="form-grid">
            <div className="form-group">
              <label>{t('EdiDestinationEmailAccount')}</label>
              <input
                type="text"
                value={msgConfig.messageDestinationEmail || ''}
                onChange={(e) => setMsgConfig({...msgConfig, messageDestinationEmail: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiSenderAccount')}</label>
              <input
                type="text"
                value={msgConfig.messageSenderCode || ''}
                onChange={(e) => setMsgConfig({...msgConfig, messageSenderCode: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiSenderEoriOptional')}</label>
              <input
                type="text"
                value={msgConfig.messageSenderEori || ''}
                onChange={(e) => setMsgConfig({...msgConfig, messageSenderEori: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiSenderContact')}</label>
              <input
                type="text"
                value={msgConfig.messageSenderContact || ''}
                onChange={(e) => setMsgConfig({...msgConfig, messageSenderContact: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiSenderEmail')}</label>
              <input
                type="text"
                value={msgConfig.messageSenderEmail || ''}
                onChange={(e) => setMsgConfig({...msgConfig, messageSenderEmail: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiReceiverAccount')}</label>
              <input
                type="text"
                value={msgConfig.messageReceiverCode || ''}
                onChange={(e) => setMsgConfig({...msgConfig, messageReceiverCode: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiCarrierAgentAccount')}</label>
              <input
                type="text"
                value={msgConfig.carrierAgentCode || ''}
                onChange={(e) => setMsgConfig({...msgConfig, carrierAgentCode: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiOutputFolder')}</label>
              <input
                type="text"
                value={msgConfig.outputFolderPath || ''}
                onChange={(e) => setMsgConfig({...msgConfig, outputFolderPath: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiOutputExtension')}</label>
              <input
                type="text"
                value={msgConfig.outputFileExtension || ''}
                onChange={(e) => setMsgConfig({...msgConfig, outputFileExtension: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiParameter001')}</label>
              <input
                type="text"
                value={msgConfig.code001 || ''}
                onChange={(e) => setMsgConfig({...msgConfig, code001: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiParameter002')}</label>
              <input
                type="text"
                value={msgConfig.code002 || ''}
                onChange={(e) => setMsgConfig({...msgConfig, code002: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiParameter003')}</label>
              <input
                type="text"
                value={msgConfig.code003 || ''}
                onChange={(e) => setMsgConfig({...msgConfig, code003: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiParameter004')}</label>
              <input
                type="text"
                value={msgConfig.code004 || ''}
                onChange={(e) => setMsgConfig({...msgConfig, code004: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiParameter005')}</label>
              <input
                type="text"
                value={msgConfig.code005 || ''}
                onChange={(e) => setMsgConfig({...msgConfig, code005: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiParameter006')}</label>
              <input
                type="text"
                value={msgConfig.code006 || ''}
                onChange={(e) => setMsgConfig({...msgConfig, code006: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiParameter007')}</label>
              <input
                type="text"
                value={msgConfig.code007 || ''}
                onChange={(e) => setMsgConfig({...msgConfig, code007: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiParameter008')}</label>
              <input
                type="text"
                value={msgConfig.code008 || ''}
                onChange={(e) => setMsgConfig({...msgConfig, code008: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiParameter009')}</label>
              <input
                type="text"
                value={msgConfig.code009 || ''}
                onChange={(e) => setMsgConfig({...msgConfig, code009: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiParameter010')}</label>
              <input
                type="text"
                value={msgConfig.code010 || ''}
                onChange={(e) => setMsgConfig({...msgConfig, code010: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiParameter031')}</label>
              <input
                type="text"
                value={msgConfig.code031 || ''}
                onChange={(e) => setMsgConfig({...msgConfig, code031: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t('EdiOutputFileCounter')}</label>
              <input
                type="number"
                value={msgConfig.outputFileCounter || 0}
                disabled
              />
            </div>
          </div>
          <div className="form-actions">
            <button
              className="btn btn-primary"
              onClick={handleSaveConfig}
              disabled={loading || !selectedMessageType || !selectedSubType}
            >
              {loading ? t('EdiSaving') : t('EdiSaveConfig')}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default EDIPage;